import React, { useState } from "react";
import { Button } from "../../../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../../design/dialog";
import { Input } from "../../../../../design/input";
import {
  Field,
  Label,
  ErrorMessage,
} from "../../../../../design/fieldset";
import { useParent } from "../../../../../contexts/ParentContext";
import { Timestamp } from "firebase/firestore";
import { useCase } from "../../../../../contexts/CaseContext";
import { Textarea } from "../../../../../design/textarea";
import { Switch, SwitchField } from "../../../../../design/switch";
import { Select } from "../../../../../design/select";
import {
  Address,
  RelationshipToChild,
  CustodyStatus,
} from "@visitproof/types";
import PhoneNumberInput from "../../../../../components/PhoneNumberInput";
import AddressSection from "../../../../../components/AddressSection";

interface Props {
  caseId: string;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  dob?: string;
  relationshipToChild?: string;
  custodyStatus?: string;
  saveError?: string;
}

const CreateParentButton: React.FC<Props> = ({ caseId }) => {
  const { createParent } = useParent();
  const { setSelectedCase } = useCase();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    phoneNumber: "",
    email: "",
    relationshipToChild: "mother" as RelationshipToChild,
    custodyStatus: CustodyStatus.Custodial,
    mentalHealthConcerns: false,
    isCurrentlyReceivingTherapy: false,
    historyOfSubstanceAbuse: false,
    notes: "",
  });
  const [address, setAddress] = useState<Address | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = (): boolean => {
    const newErrors: Errors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required.";
    if (!formData.lastName.trim())
      newErrors.lastName = "Last name is required.";
    // DOB is optional for adults (caregivers/foster parents may decline to provide)
    if (formData.dob && isNaN(Date.parse(formData.dob)))
      newErrors.dob = "Invalid date format.";
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Phone number is required.";
    if (
      !formData.phoneNumber &&
      !/^\+?[0-9]{10,15}$/.test(formData.phoneNumber)
    )
      newErrors.phoneNumber = "Invalid phone number.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email address.";
    if (!formData.relationshipToChild)
      newErrors.relationshipToChild = "Relationship to child is required.";
    if (!formData.custodyStatus)
      newErrors.custodyStatus = "Custody status is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      await createParent({
        ...formData,
        phoneNumber: `+${formData.phoneNumber.replace(/\D/g, "")}`, // Remove non-digit characters
        // DOB is optional - only convert to Timestamp if provided
        dob: formData.dob ? Timestamp.fromDate(new Date(formData.dob)) : undefined,
        address: address || undefined,
        caseId,
      });
      setSelectedCase(caseId);
      clearForm();
    } catch (error) {
      setErrors({ saveError: (error as Error).message });
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      dob: "",
      phoneNumber: "",
      email: "",
      relationshipToChild: RelationshipToChild.Mother,
      custodyStatus: CustodyStatus.Custodial,
      mentalHealthConcerns: false,
      isCurrentlyReceivingTherapy: false,
      historyOfSubstanceAbuse: false,
      notes: "",
    });
    setAddress(undefined);
    setIsOpen(false);
    setErrors({});
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Create Parent
      </Button>
      <Dialog open={isOpen} onClose={clearForm} size="2xl">
        <DialogTitle>Create Parent</DialogTitle>
        <DialogBody className="space-y-8">
          {errors.saveError && (
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <ErrorMessage>{errors.saveError}</ErrorMessage>
            </div>
          )}

          {/* Personal Information */}
          <section className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Personal Information
              </h4>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Basic details about this parent or guardian
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field>
                <Label>First Name</Label>
                <Input
                  type="text"
                  value={formData.firstName}
                  placeholder="Enter first name"
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                />
                {errors.firstName && (
                  <ErrorMessage>{errors.firstName}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  value={formData.lastName}
                  placeholder="Enter last name"
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                />
                {errors.lastName && (
                  <ErrorMessage>{errors.lastName}</ErrorMessage>
                )}
              </Field>
            </div>
            <Field>
              <Label>Date of Birth (Optional)</Label>
              <Input
                type="date"
                value={formData.dob}
                onChange={(e) => handleInputChange("dob", e.target.value)}
                className="sm:max-w-xs"
              />
              {errors.dob && <ErrorMessage>{errors.dob}</ErrorMessage>}
            </Field>
          </section>

          <hr className="border-zinc-200 dark:border-zinc-700" />

          {/* Contact Information */}
          <section className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Contact Information
              </h4>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                How to reach this parent
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  placeholder="email@example.com"
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  autoComplete=""
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Phone Number</Label>
                <PhoneNumberInput
                  defaultValue={formData.phoneNumber}
                  value={formData.phoneNumber}
                  placeholder="(555) 555-5555"
                  onChange={(value) => handleInputChange("phoneNumber", value)}
                />
                {errors.phoneNumber && (
                  <ErrorMessage>{errors.phoneNumber}</ErrorMessage>
                )}
              </Field>
            </div>
          </section>

          <hr className="border-zinc-200 dark:border-zinc-700" />

          {/* Address */}
          <section className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Address
              </h4>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Home address (optional)
              </p>
            </div>
            <AddressSection
              address={address}
              onChange={setAddress}
              disabled={isSaving}
            />
          </section>

          <hr className="border-zinc-200 dark:border-zinc-700" />

          {/* Case Information */}
          <section className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Case Information
              </h4>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Relationship and custody details
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field>
                <Label>Relationship to Child</Label>
                <Select
                  value={formData.relationshipToChild}
                  onChange={(e) =>
                    handleInputChange(
                      "relationshipToChild",
                      e.target.value as RelationshipToChild,
                    )
                  }
                >
                  {Object.entries(RelationshipToChild).map(([key, value]) => (
                    <option key={key} value={value}>
                      {key}
                    </option>
                  ))}
                </Select>
                {errors.relationshipToChild && (
                  <ErrorMessage>{errors.relationshipToChild}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Custody Status</Label>
                <Select
                  value={formData.custodyStatus}
                  onChange={(e) =>
                    handleInputChange(
                      "custodyStatus",
                      e.target.value as CustodyStatus,
                    )
                  }
                >
                  {Object.entries(CustodyStatus).map(([key, value]) => (
                    <option key={key} value={value}>
                      {key}
                    </option>
                  ))}
                </Select>
                {errors.custodyStatus && (
                  <ErrorMessage>{errors.custodyStatus}</ErrorMessage>
                )}
              </Field>
            </div>
          </section>

          <hr className="border-zinc-200 dark:border-zinc-700" />

          {/* Health & History */}
          <section className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Health & History
              </h4>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Relevant health and background information
              </p>
            </div>
            <div className="space-y-5">
              <SwitchField>
                <Label>Mental Health Concerns</Label>
                <Switch
                  checked={formData.mentalHealthConcerns}
                  onChange={() =>
                    handleInputChange(
                      "mentalHealthConcerns",
                      !formData.mentalHealthConcerns,
                    )
                  }
                />
              </SwitchField>
              <SwitchField>
                <Label>Currently Receiving Therapy</Label>
                <Switch
                  checked={formData.isCurrentlyReceivingTherapy}
                  onChange={() =>
                    handleInputChange(
                      "isCurrentlyReceivingTherapy",
                      !formData.isCurrentlyReceivingTherapy,
                    )
                  }
                />
              </SwitchField>
              <SwitchField>
                <Label>History of Substance Abuse</Label>
                <Switch
                  checked={formData.historyOfSubstanceAbuse}
                  onChange={() =>
                    handleInputChange(
                      "historyOfSubstanceAbuse",
                      !formData.historyOfSubstanceAbuse,
                    )
                  }
                />
              </SwitchField>
            </div>
          </section>

          <hr className="border-zinc-200 dark:border-zinc-700" />

          {/* Notes */}
          <section className="space-y-6">
            <div>
              <h4 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                Additional Notes
              </h4>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Any other relevant information about this parent
              </p>
            </div>
            <Field>
              <Textarea
                rows={4}
                value={formData.notes}
                placeholder="Enter any additional notes, concerns, or relevant information..."
                onChange={(e) => handleInputChange("notes", e.target.value)}
              />
            </Field>
          </section>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateParentButton;
